import getSdks from "./sdks";
import getWindow from "./window";
import getConstants from "../runtimeConstants";
import { ERROR_TYPE, logError } from "./newRelicLogger";

const FACE_AND_BODY_CANONICAL = "Face & Body";

function sanitiseFaceAndBody(pdpLayout) {
  const layout = pdpLayout?.toLowerCase();
  return layout === "face & body" || layout === "face + body"
    ? FACE_AND_BODY_CANONICAL
    : pdpLayout;
}

let pdpFixedAttributes;

function getPdpFixedAttributes() {
  if (!pdpFixedAttributes) {
    try {
      const { product } = getConstants();
      const pdpLayout = sanitiseFaceAndBody(product?.pdpLayout);
      pdpFixedAttributes = {
        app: "pdp",
        ...(pdpLayout != null && { pdpLayout }),
      };
    } catch {
      pdpFixedAttributes = {
        app: "pdp",
      };
    }
  }
  return pdpFixedAttributes;
}

let resolveSdkPromise;

const getFeaturesFromSdk = {
  getFeature: () => ({
    isEnabled: () => false,
    /** @returns {Record<string, string> | null} */
    getVariables: () => null,
  }),
  track: () => null,
  getCoreAttributes: () => null,
  setCoreAttributes: () => null,
};

const onReadyPromise = new Promise((resolve) => {
  resolveSdkPromise = resolve;
});

export const onReady = () => onReadyPromise;

const enableEdgeMVT = ({ isMixAndMatch, isInStock, isEnabled }) => {
  if (isMixAndMatch === false && isInStock === false) {
    isEnabled();
  }
};

export const initialiseFeatures = (isMixAndMatch, isInStock) => {
  getSdks()
    ?.features?.then(async (featureSdk) => {
      getFeaturesFromSdk.getFeature =
        featureSdk.getFeature ?? getFeaturesFromSdk.getFeature;
      getFeaturesFromSdk.track = featureSdk.track ?? getFeaturesFromSdk.track;
      getFeaturesFromSdk.getCoreAttributes =
        featureSdk.getCoreAttributes ?? getFeaturesFromSdk.getCoreAttributes;
      getFeaturesFromSdk.setCoreAttributes =
        featureSdk.setCoreAttributes ?? getFeaturesFromSdk.setCoreAttributes;

      const existingAttributes = featureSdk.getCoreAttributes?.() ?? {};
      featureSdk.setCoreAttributes?.({
        ...existingAttributes,
        ...getPdpFixedAttributes(),
      });

      await featureSdk.onReady();
      resolveSdkPromise(); // this resolution must happen last to avoid race conditions
    })
    ?.catch(() => {
      logError(ERROR_TYPE.EdgeError, {
        action: "getSdks.features",
        message: "Error getting feature SDK",
      });
    });

  onReady()
    .then(() => {
      if (getWindow().asos.pdp.config.edgeFeature) {
        const { featureId, bucket: serverBucket } =
          getWindow().asos.pdp.config.edgeFeature;

        const { isEnabled, getVariables } = getFeature(featureId);

        const clientBucket = getVariables()?.bucket;
        if (clientBucket !== serverBucket) {
          logError(ERROR_TYPE.EdgeError, {
            featureId,
            serverBucket,
            clientBucket,
            action: "edgeMVTVariables",
            message: `Server bucket [${serverBucket}] does not match bucket returned on the client [${clientBucket}]`,
          });
        } else {
          enableEdgeMVT({ isMixAndMatch, isInStock, isEnabled });
        }
      }
    })
    .catch(() => {
      const { featureId } = getWindow().asos.pdp.config.edgeFeature;
      logError(ERROR_TYPE.EdgeError, {
        featureId,
        action: "features.onReady",
        message: "Error while feature.onReady",
      });
    });
};

export const getFeature = (key, id, attributes) =>
  getFeaturesFromSdk.getFeature(key, id, {
    ...getPdpFixedAttributes(),
    ...attributes,
  });
export const track = (...args) => getFeaturesFromSdk.track(...args);
export const getCoreAttributes = (...args) =>
  getFeaturesFromSdk.getCoreAttributes(...args);
export const setCoreAttributes = (...args) =>
  getFeaturesFromSdk.setCoreAttributes(...args);
